import { createContext, FC, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FilterType, ProviderNetworkType, RegionSetStatusType } from "../../../enumerators";
import { CountyProjectContext, RegionFilterItem } from "../../../CountyProjectContext";
import { RegionGranularity } from "../../../../region-sets";
import ProjectService from "@app/projects/projectService";
import { AnalysisSpecialtyGroup } from "../../../results/common";
import { History } from "history";
import { DropdownItem } from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { GeographyService } from "@app/geography";

type ServiceArea = {
  displayName: string;
  regionCode: string;
  regionGranularity: RegionGranularity;
  regionSetId: number;
  subRegions: {
    displayName: string;
    regionCode: string;
    regionGranularity: RegionGranularity;
    regionSetId: number;
    status: RegionSetStatusType;
  }[],
}

interface ProviderSourceItem {
  name: string;
  type?: ProviderNetworkType;
  children?: { name: string; type: ProviderNetworkType }[];
}

interface FilterContextProps {
  filterProviderSource: Array<DropdownItem<string>>;
  filterServiceArea: string[];
  filterSpecialty: Array<DropdownItem<string>>;
  getCountyFilterValue: (filters: RegionFilterItem[], filterTypeValue: FilterType) => string;
  getCurrentServiceAreaStatus: () => RegionSetStatusType;
  getFilterValue: (filters: RegionFilterItem[], filterTypeValue: FilterType) => string;
  getPathParameter: (path: string, param: string) => string;
  history: History;
  isFilterActive: boolean;
  mapButtonsAreDisabled: boolean;
  providerSource: ProviderSourceItem[];
  replaceFilterUrl: (filterType: FilterType, currentValue: string, newValue: string) => void;
  serviceArea: ServiceArea[];
  selectedProviderSources: ProviderNetworkType[];
  selectedServiceArea: string;
  selectedSpecialty: string;
  serviceAreaRegionIdToRegionCodeMap: Map<string, { countyCode: string, stateCode: string }>;
  setFilterProviderSource: (providerSource: Array<DropdownItem<string>>) => void;
  setFilterServiceArea: (serviceArea: string[]) => void;
  setFilterSpecialty: (specialty: Array<DropdownItem<string>>) => void;
  setIsFilterActive: (active: boolean) => void,
  setMapButtonsAreDisabled: (disabled: boolean) => void;
  setSelectedProviderSources: (providerSource: ProviderNetworkType[]) => void;
  setSelectedServiceArea: (serviceArea: string) => void;
  setSelectedSpecialty: (specialty: string) => void;
  sortedSpecialtyGroups: AnalysisSpecialtyGroup[];
}

const initialValues: FilterContextProps = {
  filterProviderSource: new Array<DropdownItem<string>>(),
  filterServiceArea: [],
  filterSpecialty: new Array<DropdownItem<string>>(),
  getCountyFilterValue: () => null,
  getCurrentServiceAreaStatus: () => null,
  getFilterValue: () => null,
  getPathParameter: () => null,
  history: null,
  isFilterActive: false,
  mapButtonsAreDisabled: true,
  providerSource: null,
  replaceFilterUrl: () => null,
  selectedProviderSources: [ProviderNetworkType.Network],
  selectedServiceArea: null,
  selectedSpecialty: null,
  serviceArea: null,
  serviceAreaRegionIdToRegionCodeMap: new Map(),
  setFilterProviderSource: () => null,
  setFilterServiceArea: () => null,
  setFilterSpecialty: () => null,
  setIsFilterActive: () => null,
  setMapButtonsAreDisabled: () => null,
  setSelectedProviderSources: () => null,
  setSelectedServiceArea: () => null,
  setSelectedSpecialty: () => null,
  sortedSpecialtyGroups: null,
}

const FilterContext = createContext(initialValues);

const FilterContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const projectContext = useContext(CountyProjectContext);
  const projectService = useRef(new ProjectService()).current;
  const georgraphyService = useRef(new GeographyService()).current;

  const history = useHistory();
  const location = useLocation();

  // Filter Options
  const [filterProviderSource, setFilterProviderSource] = useState<Array<DropdownItem<string>>>(initialValues.filterProviderSource);
  const [filterServiceArea, setFilterServiceArea] = useState<string[]>(initialValues.filterServiceArea);
  const [filterSpecialty, setFilterSpecialty] = useState<Array<DropdownItem<string>>>(initialValues.filterSpecialty);
  const [mapButtonsAreDisabled, setMapButtonsAreDisabled] = useState(false);
  const [selectedServiceArea, setSelectedServiceArea] = useState<string>('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [selectedProviderSources, setSelectedProviderSources] = useState<ProviderNetworkType[]>([ProviderNetworkType.Network]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [serviceArea, setServiceArea] = useState<ServiceArea[]>([]);
  const serviceAreaRegionIdToRegionCodeMap = useRef<Map<string, { countyCode: string, stateCode: string }>>(new Map());

  const getCurrentServiceAreaStatus = (): RegionSetStatusType => {
    const county = serviceAreaRegionIdToRegionCodeMap.current.get(selectedServiceArea);
    if (!county) return RegionSetStatusType.Unknown;

    const s = serviceArea.find(sa => sa.subRegions.some(sr => sr.regionCode === county.countyCode));

    if (!s) return RegionSetStatusType.Unknown;

    const subRegion = s.subRegions.find(sr => sr.regionCode === county.countyCode);
    if (!subRegion) return RegionSetStatusType.Unknown;

    return subRegion.status || RegionSetStatusType.Unknown;
  }

  const providerSource: ProviderSourceItem[] = [
    { name: 'Network Servicing Providers', type: ProviderNetworkType.Network },
  ];

  const outOfNetworkOptions: { name: string, type: ProviderNetworkType }[] = [];

  if (!!projectContext.runConfiguration.prospectProviderSetId) {
    outOfNetworkOptions.push({ name: 'Prospect Providers', type: ProviderNetworkType.Prospect });
  }
  if (!!projectContext.runConfiguration.marketProviderSetId) {
    outOfNetworkOptions.push({ name: 'Market Providers', type: ProviderNetworkType.Market });
  }

  if (outOfNetworkOptions.length > 0) {
    providerSource.push({
      children: outOfNetworkOptions,
      name: 'Out of Network Providers',
    });
  }

  const sortedSpecialtyGroups = (
    projectContext.runConfiguration.specialtyGroups || []).sort((a, b) => a.name.localeCompare(b.name)
    );

  const replaceFilterUrl = (filterType: FilterType, currentValue: string, newValue: string) => {
    if (!newValue) {
      if (!currentValue) return;

      if (history.location.pathname.includes(filterType)) {
        history.push(history.location.pathname
          .replaceAll(filterType + currentValue, "")
          .replaceAll(filterType + getPathParameter(
            history.location.pathname,
            filterType.replace(/\//g, "")), ""));
      }
    } else if (history.location.pathname.includes(filterType)) {
      history.push(history.location.pathname
        .replaceAll(filterType + currentValue, "")
        .replaceAll(filterType + getPathParameter(
          history.location.pathname,
          filterType.replace(/\//g, "")), "")
        .concat(filterType + newValue));
    } else {
      history.push(`${history.location.pathname}${filterType + newValue}`);
    }

    projectContext.loadedFilters = false;
  }

  const getPathParameter = (path: string, param: string): string => {
    const pathSplit = path.split('/');
    const paramIndex = pathSplit.indexOf(param);

    if (paramIndex > -1 && paramIndex < pathSplit.length - 1) return pathSplit[paramIndex + 1];
  }

  const getFilterValue = (filters: RegionFilterItem[], filterTypeValue: FilterType): string => {
    const filterItem = filters.find(filter => filter.filterType === filterTypeValue);

    if (!!filterItem) return filterItem.filterValue;

    // If no county filter is found, it might still be in the url.  Attempt to parse it from the url.
    const filterPathValue = getPathParameter(location?.pathname, filterTypeValue.replace(/\//g, ""));

    if (!filterPathValue) return '';
    return filterPathValue;
  };

  const getCountyFilterValue = (filters: RegionFilterItem[], filterTypeValue: FilterType): string => {
    const countyFilterValue = getFilterValue(filters, filterTypeValue);

    if (!countyFilterValue) return '';

    //Since the value is a county code, we have to dig for the region set id with our code
    const countyMapEntry = [...serviceAreaRegionIdToRegionCodeMap.current].find(([, value]) => value.countyCode === countyFilterValue);
    return !!countyMapEntry ? countyMapEntry[0] : '';
  };

  useEffect(() => {
    // Get all the counties for the project
    const getServiceAreas = async () => {
      // A list of states and their counties as children
      const countiesByState = await projectService.getCountiesByRunConfigurationId(projectContext.project.projectId.toString(), projectContext.runConfiguration.projectRunConfigurationId.toString(), false);
      const statesList = await georgraphyService.getStatesForDroplist();
      const availableStatuses = await projectService.getAvailableStatuses(projectContext.project.projectId, projectContext.runConfiguration.projectRunConfigurationId);

      const serviceAreas: ServiceArea[] = countiesByState.map(state => {
        const stateInfo = availableStatuses.find(s => s.regionCode === state.key);
        // State name in `state` is the abbreviation, so we need to find the full name
        const stateName = statesList.find(s => s.abbreviation === state.key)?.name || state.name;

        return {
          displayName: stateName,
          regionCode: state.name,
          regionGranularity: stateInfo.regionGranularity,
          regionSetId: stateInfo.regionSetId,
          subRegions: state.children.map(county => {
            const countyInfo = availableStatuses.find(s => s.regionSetId === Number(county.key));
            if (!countyInfo) {
              return;
            }

            serviceAreaRegionIdToRegionCodeMap.current.set(
              county.key,
              {
                countyCode: countyInfo.regionCode,
                stateCode: state.key
              });

            return {
              displayName: county.name,
              regionCode: countyInfo.regionCode,
              regionGranularity: countyInfo.regionGranularity,
              regionSetId: countyInfo.regionSetId,
              status: countyInfo.availableStatuses ? countyInfo.availableStatuses[0] : RegionSetStatusType.Unknown,
            };
          }).filter(sr => !!sr), // Filter out any undefined sub-regions
        }
      });

      setServiceArea(serviceAreas);
    }

    getServiceAreas().then(() => {
      setSelectedSpecialty(getFilterValue(projectContext.filters, FilterType.Specialty));
      setSelectedServiceArea(getCountyFilterValue(projectContext.filters, FilterType.County));
      setSelectedProviderSources([ProviderNetworkType.Network]);
    });
  }, [])

  useEffect(() => {
    setSelectedSpecialty(getFilterValue(projectContext.filters, FilterType.Specialty));
    setSelectedServiceArea(getCountyFilterValue(projectContext.filters, FilterType.County));
  }, [projectContext.filters]);

  useEffect(() => {
    setMapButtonsAreDisabled(!(!!selectedServiceArea && !!selectedSpecialty && !!selectedProviderSources.length));
  }, [selectedServiceArea, selectedSpecialty, selectedProviderSources]);

  const contextValue: FilterContextProps = {
    filterProviderSource,
    filterServiceArea,
    filterSpecialty,
    getCountyFilterValue,
    getCurrentServiceAreaStatus,
    getFilterValue,
    getPathParameter,
    history,
    isFilterActive,
    mapButtonsAreDisabled,
    providerSource,
    replaceFilterUrl,
    selectedProviderSources,
    selectedServiceArea,
    selectedSpecialty,
    serviceArea,
    serviceAreaRegionIdToRegionCodeMap: serviceAreaRegionIdToRegionCodeMap.current,
    setFilterProviderSource,
    setFilterServiceArea,
    setFilterSpecialty,
    setIsFilterActive,
    setMapButtonsAreDisabled,
    setSelectedProviderSources,
    setSelectedServiceArea,
    setSelectedSpecialty,
    sortedSpecialtyGroups,
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {props.children}
    </FilterContext.Provider>
  );
};

export { FilterContext, FilterContextProvider, FilterContextProps };